import type { AuthSessionResponse, TokenResponse } from '@uznali/api-client';
import { z } from 'zod';

export const sessionMetadataSchema = z.object({
  sessionType: z.enum(['account', 'guest']),
  platform: z.string().min(1),
  capabilities: z.object({
    accountRewards: z.boolean(),
    cloudProgress: z.boolean(),
    guestAccumulation: z.boolean(),
    purchases: z.boolean(),
    rankings: z.boolean(),
  }),
  guestId: z.string().nullish(),
  user: z
    .object({
      login: z.string().regex(/^[a-zA-Z0-9_-]+$/),
      profileName: z.string().nullish(),
      avatarUrl: z.string().nullish(),
      tmpLogin: z.boolean().optional(),
    })
    .passthrough()
    .nullish(),
  serverNow: z.string(),
  tmpLogin: z.boolean().optional(),
});

export const sessionTokensSchema = z
  .object({
    accessToken: z.string().min(1),
    refreshToken: z.string().min(1),
  })
  .extend(sessionMetadataSchema.partial().shape);

export type SessionMetadata = z.infer<typeof sessionMetadataSchema>;
export type StoredTokens = z.infer<typeof sessionTokensSchema>;

const tokenKey = 'uznali-tokens';
const temporaryTokenKey = 'uznali-temporary-tokens';

const parseTokens = (value: string | null): StoredTokens | null => {
  if (!value) return null;
  try {
    const parsed = sessionTokensSchema.safeParse(JSON.parse(value));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
};

export const tokenStore = {
  get(): StoredTokens | null {
    return parseTokens(sessionStorage.getItem(tokenKey)) ?? parseTokens(localStorage.getItem(tokenKey));
  },
  getForRequest(): StoredTokens | null {
    return parseTokens(sessionStorage.getItem(temporaryTokenKey)) ?? this.get();
  },
  getTemporary(): StoredTokens | null {
    return parseTokens(sessionStorage.getItem(temporaryTokenKey));
  },
  setTemporary(tokens: StoredTokens | null) {
    sessionStorage.removeItem(temporaryTokenKey);
    if (tokens) sessionStorage.setItem(temporaryTokenKey, JSON.stringify(tokens));
  },
  updateFromRefresh(tokens: TokenResponse | AuthSessionResponse | null) {
    const current = this.getForRequest();
    // The SDK also clears credentials on network failures. Preserve the guest
    // identity so retrying cannot silently create a replacement guest.
    if (!tokens && current?.sessionType === 'guest' && !this.getTemporary()) return;
    const parsed = tokens ? sessionTokensSchema.parse({ ...current, ...tokens }) : null;
    if (this.getTemporary()) this.setTemporary(parsed);
    else this.set(parsed);
  },
  updateMetadata(metadata: SessionMetadata, expectedTokens: TokenResponse): boolean {
    const current = this.getForRequest();
    if (
      !current ||
      current.accessToken !== expectedTokens.accessToken ||
      current.refreshToken !== expectedTokens.refreshToken
    )
      return false;
    const next = { ...current, ...metadata };
    if (this.getTemporary()) this.setTemporary(next);
    else this.set(next);
    return true;
  },
  set(tokens: StoredTokens | null, remember = Boolean(localStorage.getItem(tokenKey))) {
    sessionStorage.removeItem(tokenKey);
    localStorage.removeItem(tokenKey);
    if (tokens) (remember ? localStorage : sessionStorage).setItem(tokenKey, JSON.stringify(tokens));
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('uznali-session-changed'));
  },
};
